/* eslint-disable no-unused-vars */
import { AppContext, getToken, withAppContext } from '@ctsu/loader';
import { NavSpine } from '@ctsu/nav-spine';
import { Footer, Header, Notification } from '@ctsu/react-ctsu-ui';
import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { matchPath } from 'react-router';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { getShowSummarySelector, getSummaryDataSelector } from 'slices/headerSummarySlice';
import ErrorBoundary from './ErrorBoundry';
import buildInfoLock from './components/BuildInfo/buildInfoLock.json';
import { GotoLogin } from './components/Login';
import UserTypePicker from './components/UserTypePicker';
import { text } from './components/common/Text';
import ToasterMessage from './components/common/ToasterMessage';
import Unauthorized from './components/common/Unauthorized';

import {
  filterUnavailableRoutes,
  flattenRoutes,
  getUserTypeFromStorage,
  updateRoutesAvailability
} from './helper/utils';
import appRoutes from './routes/appRoutes';
import * as C from './routes/constants';

function PageViews() {
  const location = useLocation();
  useEffect(() => {
    if ('ga' in window) {
      const { ga } = window;
      ga('set', 'page', location.pathname);
      ga('send', 'pageview');
    }
  }, [location]);

  return null;
}

function LogoutCmp() {
  return <NavLink to={C.LOGOUT_PATH}>Logout</NavLink>;
}

function RedirectIfUnavailable({ routes, isAuthenticated, children }) {
  const location = useLocation();
  const flatRoutes = flattenRoutes(routes);
  // Use matchPath to match on variable path routes (those with /:guid)
  const { isAvailable = true, title } =
    flatRoutes.find((r) =>
      matchPath({ path: r.path, exact: true }, location.pathname)
    ) || {};

  if (isAvailable) {
    return children;
  }
  if (isAuthenticated) {
    const userType = getUserTypeFromStorage();
    return (
      <Unauthorized
        message={text('common.no_user_type_message', `${userType}`, `${title}`)}
      />
    );
  }
  return <GotoLogin />;
}

const buildInfo = {
  buildVersion: buildInfoLock.build_version,
  buildDate: buildInfoLock.build_date
};
function DTLHeader({
  showUserTypeDd = true,
  filteredRoutes
}) {
  const context = useContext(AppContext);

  const { session, application } = context;

  const {
    userPermissions,
    pwdExpiryDays,
    username,
    impersonateLevel,
    userFirstLast,
    lastLogin,
    ctepId
  } = session || [];
  const { dbName, appName = '' } = application || [];

  const showSummary = useSelector(getShowSummarySelector)
  const summaryData = useSelector(getSummaryDataSelector).filter((kvp) => kvp.isVisible !== false )

  return (
    <Header
      key="header"
      appName={appName}
      displayAppName="DTL"
      subName="Delegation of Tasks Log"
      routes={filteredRoutes}
      logout={LogoutCmp}
      userPermissions={userPermissions}
      dbName={dbName}
      impersonateContext={context}
      userFirstLast={userFirstLast}
      coreVersion="v1"
      fnToken={getToken}
      username={username}
      impersonateLevel={impersonateLevel}
      lastLogin={lastLogin}
      ctepId={ctepId}
      pwdExpiryDays={pwdExpiryDays}
      leftNavItems={showUserTypeDd ? UserTypePicker : null}
      showSummary={showSummary}
      stickyData={summaryData}
    />
  );
}

function AppContent({ context, children}) {
  const [allRoutes, setAllRoutes] = useState(appRoutes);
  const [filteredRoutes, setFilteredRoutes] = useState(appRoutes);
  const authenticated = context && !!getToken();
  const { session, application } = context;
  const { dbName } = application || [];
  const userType = getUserTypeFromStorage();
  useEffect(() => {
    const checkUserType = (condition) => condition.indexOf(userType) > -1;
    const allRoutesTemp = updateRoutesAvailability(appRoutes, checkUserType);
    setAllRoutes(allRoutesTemp);
    const filteredRoutesTemp = filterUnavailableRoutes(allRoutesTemp);
    setFilteredRoutes(filteredRoutesTemp);
  }, [userType]);

  return (
    <>
      {authenticated && (
        <div className="skip-to-content">
          <a
            className="sr-only sr-only-focusable color-white"
            href="#main-content"
            title="Skip to content"
          >
            Skip Navigation
          </a>
        </div>
      )}
      <section id="dtl-app-header">
        <DTLHeader
          filteredRoutes={filteredRoutes}
        />
      </section>
      {authenticated ? (
        <NavSpine
          userContext={session}
          appRoot={process.env.REACT_APP_ORIGIN}
          appName="DTL"
          getToken={getToken}
        />
      ) : null}
      <Notification
        fnToken={getToken}
        appName={application.appName}
        isMaintainer
        coreVersion={`${process.env.REACT_APP_PERSON_API_VERSION}`}
        appContext={context}
        isPublic
      />
      <div id="main-content" className="main app-body">
        <div className="app-spacing">
          <ErrorBoundary>
            <div className="app-body-content-no-dash">
              <RedirectIfUnavailable
                routes={allRoutes}
                isAuthenticated={authenticated}
              >
                {children}
              </RedirectIfUnavailable>
              <ToasterMessage />
              <Outlet />
            </div>
          </ErrorBoundary>
        </div>
      </div>
      <Footer
        dbName={dbName || ''}
        buildInfo={buildInfo}
        releaseNotes="https://www.ctsu.org/Master/SimplePage.aspx?ckey=DTL-RELEASE-NOTES"
      />
      <PageViews />
    </>
  );
}
export default withAppContext(AppContent);
